// doneness — deterministic thickness + target-temp model for the
// pre-cook questionnaire. The cook tells us how thick the protein is and
// how done they want it; we return the TARGET INTERNAL TEMP (the source
// of truth) plus a thickness-scaled timing ESTIMATE for the sear.
//
// First principle: internal temperature is truth, the timer is a guess.
// A thermometer pulled at the target temp is always right; minutes-per-
// side depends on pan heat, fat content, starting temp, and the stove —
// things we can't see. So every surface that uses this must present the
// temp as the headline and the time as "roughly N min/side, check the
// thermometer." We NEVER pretend the clock is authoritative.
//
// This is hand-curated cooking data, not an AI call — hallucinated
// internal temps are a food-safety problem (see meatDoneness.js, which
// carries the USDA safe-floor temps this builds on). Numbers here are
// standard final temps; we pull a few degrees early to leave room for
// carryover on thicker cuts.
//
// Scope: this questionnaire makes sense for QUICK-COOK, thickness-driven
// proteins — steaks, chops, fillets, poultry pieces. Braises (brisket,
// pork shoulder, short ribs) stay excluded: their doneness is about
// time/safe-temp, not thickness, and meatDoneness.js stamps their cues.
//
// Ground-meat PATTIES (burgers) are included — but differently. A burger
// has NO doneness spectrum: ground meat mixes surface bacteria all the
// way through, so it's cooked to a single USDA safe floor (160°F red,
// 165°F poultry), never rare/medium. Thickness still matters though — a
// smashburger and a thick pub patty cook in wildly different times — so
// for patties we ask thickness only, lock the doneness at the safe temp,
// and scale the TIME estimate. See PATTY_FAMILIES below.

// ── Protein families ────────────────────────────────────────────────
// Each thickness-relevant canonical maps to a family that defines its
// doneness options + base sear timing. Families, not per-canonical, so
// "ribeye" and "strip_steak" share one table.
const CANONICAL_FAMILY = {
  // Beef + lamb steaks — the full rare→well doneness spectrum.
  steak: "red_meat",
  ribeye: "red_meat",
  strip_steak: "red_meat",
  ny_strip: "red_meat",
  sirloin: "red_meat",
  top_sirloin: "red_meat",
  filet_mignon: "red_meat",
  tenderloin_steak: "red_meat",
  flank_steak: "red_meat",
  skirt_steak: "red_meat",
  flat_iron_steak: "red_meat",
  hanger_steak: "red_meat",
  t_bone: "red_meat",
  porterhouse: "red_meat",
  tri_tip: "red_meat",
  lamb_chop: "red_meat",
  lamb_loin: "red_meat",
  // Pork chops / loin — medium (juicy) or well.
  pork_chop: "pork",
  pork_loin: "pork",
  pork_tenderloin: "pork",
  // Poultry pieces — single safe target, thickness drives the timing.
  chicken_breast: "poultry",
  chicken_thigh: "poultry",
  chicken_tenderloin: "poultry",
  turkey_breast: "poultry",
  duck_breast: "duck",
  // Fish — firm fillets/steaks pulled at restaurant-medium or fully done.
  salmon: "fish",
  tuna: "tuna",
  cod: "fish",
  halibut: "fish",
  mahi_mahi: "fish",
  swordfish: "fish",
  trout: "fish",
  sea_bass: "fish",
  // Quick shellfish — doneness is "just opaque," thickness barely moves it.
  shrimp: "shellfish",
  scallops: "shellfish",
  // Ground-meat patties (burgers) — locked safe-temp doneness, thickness
  // drives the time only. Red meats to 160°F, poultry to 165°F.
  ground_beef: "ground_red",
  ground_pork: "ground_red",
  ground_lamb: "ground_red",
  ground_chicken: "ground_poultry",
  ground_turkey: "ground_poultry",
};

// Families whose "doneness" is a single locked USDA safe temp — ground
// meat. Thickness still scales the time estimate, but there's no rare/
// medium/well choice (food safety), and they never reverse-sear.
const PATTY_FAMILIES = new Set(["ground_red", "ground_poultry"]);

// Doneness options per family. finalF = the temp the cook is going FOR;
// pullF = pull-from-heat temp (a touch lower so carryover lands it at
// finalF). recommended flags the default chip.
const FAMILY_DONENESS = {
  red_meat: [
    { id: "rare",        label: "Rare",        finalF: 122, pullF: 118 },
    { id: "medium_rare", label: "Medium-rare", finalF: 132, pullF: 128, recommended: true },
    { id: "medium",      label: "Medium",      finalF: 142, pullF: 137 },
    { id: "medium_well", label: "Medium-well", finalF: 150, pullF: 145 },
    { id: "well",        label: "Well-done",   finalF: 160, pullF: 155 },
  ],
  pork: [
    { id: "medium", label: "Medium (juicy)", finalF: 145, pullF: 140, recommended: true },
    { id: "well",   label: "Well-done",      finalF: 160, pullF: 155 },
  ],
  poultry: [
    { id: "done", label: "Fully cooked", finalF: 165, pullF: 160, recommended: true },
  ],
  duck: [
    { id: "medium_rare", label: "Medium-rare", finalF: 135, pullF: 130, recommended: true },
    { id: "well",        label: "Well-done",   finalF: 160, pullF: 155 },
  ],
  fish: [
    { id: "medium", label: "Medium (moist)", finalF: 125, pullF: 122, recommended: true },
    { id: "well",   label: "Fully cooked",   finalF: 145, pullF: 140 },
  ],
  tuna: [
    { id: "rare",   label: "Seared rare",   finalF: 110, pullF: 110, recommended: true },
    { id: "medium", label: "Medium",        finalF: 130, pullF: 126 },
  ],
  shellfish: [
    { id: "done", label: "Opaque + just firm", finalF: 145, pullF: 145, recommended: true },
  ],
  // Ground meat — one option, the safe floor. No carryover pull: you cook
  // a burger TO temp, you don't pull-and-rest it like a steak.
  ground_red: [
    { id: "safe", label: "Cooked through", finalF: 160, pullF: 160, recommended: true },
  ],
  ground_poultry: [
    { id: "safe", label: "Cooked through", finalF: 165, pullF: 165, recommended: true },
  ],
};

// Base sear time, PER SIDE, in seconds, for a 1-inch-thick cut in a hot
// pan, at each doneness. Estimates only — see the file header. Higher
// doneness = more time on the heat. Shellfish/thin fish use the family's
// single value regardless of doneness id (keyed "*").
const FAMILY_BASE_PER_SIDE_SEC = {
  red_meat:  { rare: 120, medium_rare: 165, medium: 210, medium_well: 255, well: 300 },
  pork:      { medium: 210, well: 270 },
  poultry:   { done: 300 },                 // ~5 min/side for a 1" breast
  duck:      { medium_rare: 240, well: 330 }, // skin-side render runs long
  fish:      { medium: 180, well: 240 },
  tuna:      { rare: 60, medium: 120 },
  shellfish: { done: 90 },
  // Patties at a 1" reference; thickness scaling does the rest. A typical
  // 0.75" burger lands ~2.5-3 min/side off these.
  ground_red:     { safe: 210 },
  ground_poultry: { safe: 240 }, // poultry needs the higher 165°F floor
};

// Above this thickness a straight sear overcooks the outside before the
// center comes up — recommend the reverse-sear / finish-in-oven method.
const REVERSE_SEAR_THRESHOLD_IN = 1.5;

// Default thickness (inches) we seed the questionnaire with per family —
// the typical cut so the common case needs no adjustment.
const FAMILY_DEFAULT_THICKNESS_IN = {
  red_meat: 1.0,
  pork: 1.0,
  poultry: 0.75,
  duck: 0.9,
  fish: 1.0,
  tuna: 1.0,
  shellfish: 0.6,
  ground_red: 0.75,      // typical hand-formed patty
  ground_poultry: 0.75,
};

/** Family id for a canonical, or null when it's not a thickness-driven cut. */
export function donenessFamilyFor(canonicalId) {
  return CANONICAL_FAMILY[canonicalId] || null;
}

/** True when this canonical gets the thickness/doneness questionnaire. */
export function isDonenessProtein(canonicalId) {
  return !!CANONICAL_FAMILY[canonicalId];
}

/** Doneness options for a canonical (array), or [] when not applicable. */
export function donenessOptionsFor(canonicalId) {
  const fam = donenessFamilyFor(canonicalId);
  return fam ? (FAMILY_DONENESS[fam] || []) : [];
}

/** The recommended/default doneness option for a canonical, or null. */
export function defaultDonenessFor(canonicalId) {
  const opts = donenessOptionsFor(canonicalId);
  return opts.find((o) => o.recommended) || opts[0] || null;
}

/** Default starting thickness (inches) to seed the picker with. */
export function defaultThicknessIn(canonicalId) {
  const fam = donenessFamilyFor(canonicalId);
  return fam ? (FAMILY_DEFAULT_THICKNESS_IN[fam] ?? 1.0) : 1.0;
}

/**
 * The temp + timing guidance for a cut at a given thickness + doneness.
 *
 * @param {object} args
 * @param {string} args.canonicalId
 * @param {number} args.thicknessIn   — thickness in inches
 * @param {string} args.donenessId    — one of donenessOptionsFor(...).id
 * @returns {null | {
 *   family: string,
 *   doneness: { id, label, finalF, pullF },
 *   thicknessIn: number,
 *   perSideSec: number,        // estimated sear time per side
 *   totalSec: number,          // perSideSec * 2 (both sides)
 *   reverseSear: boolean,      // true when the cut is too thick to straight-sear
 *   tempCue: string,           // canonical "pull at X°F" sentence for doneCue
 *   timeEstimateLabel: string, // human "~4 min per side" — clearly an estimate
 * }}
 */
export function donenessGuidance({ canonicalId, thicknessIn, donenessId } = {}) {
  const fam = donenessFamilyFor(canonicalId);
  if (!fam) return null;
  const options = FAMILY_DONENESS[fam] || [];
  const doneness =
    options.find((o) => o.id === donenessId) ||
    options.find((o) => o.recommended) ||
    options[0];
  if (!doneness) return null;

  const thickness = Number(thicknessIn) > 0 ? Number(thicknessIn) : (FAMILY_DEFAULT_THICKNESS_IN[fam] ?? 1.0);

  // Base per-side time for a 1" cut at this doneness, scaled by thickness.
  // Conduction time grows faster than linearly with thickness, but a mild
  // exponent keeps the estimate honest without overstating precision.
  const baseTable = FAMILY_BASE_PER_SIDE_SEC[fam] || {};
  const base = baseTable[doneness.id] ?? Object.values(baseTable)[0] ?? 180;
  const thicknessFactor = Math.pow(thickness / 1.0, 1.15);
  const perSideSec = Math.max(30, Math.round((base * thicknessFactor) / 5) * 5);
  const totalSec = perSideSec * 2;
  // Patties never reverse-sear — a burger that's slow to cook through gets
  // a covered pan / lower heat, not an oven finish.
  const patty = PATTY_FAMILIES.has(fam);
  const reverseSear = !patty && thickness > REVERSE_SEAR_THRESHOLD_IN;

  const perSideMin = perSideSec / 60;
  const minLabel = perSideMin >= 1
    ? `${perSideMin % 1 === 0 ? perSideMin : perSideMin.toFixed(1)} min`
    : `${perSideSec} sec`;

  return {
    family: fam,
    patty,
    doneness: { id: doneness.id, label: doneness.label, finalF: doneness.finalF, pullF: doneness.pullF },
    thicknessIn: thickness,
    perSideSec,
    totalSec,
    reverseSear,
    tempCue: tempCueFor(fam, doneness),
    timeEstimateLabel: `~${minLabel} per side`,
  };
}

// The doneCue sentence — temp first (the truth), then the visual + a
// reminder the time is just a guide.
function tempCueFor(fam, doneness) {
  const pull = `Pull at ${doneness.pullF}°F internal (${doneness.label.toLowerCase()}, ${doneness.finalF}°F after resting)`;
  if (fam === "ground_red" || fam === "ground_poultry") {
    return `Internal temp ${doneness.finalF}°F — cook ground meat all the way through, no pink. Use a thermometer; time is only a guide.`;
  }
  if (fam === "poultry") return `Internal temp ${doneness.finalF}°F at the thickest part; juices run clear. Use a thermometer — time is only a guide.`;
  if (fam === "shellfish") return `Cook until opaque and just firm (${doneness.finalF}°F). Time is only a guide.`;
  return `${pull}. Use a thermometer, not the clock — time per side is an estimate.`;
}

/**
 * Find the thickness-driven proteins in a recipe (for deciding whether to
 * show the questionnaire and which proteins to ask about). Returns a
 * de-duped array of { canonicalId, name } in ingredient order.
 *
 * @param {object} recipe
 * @param {(id:string)=>({name?:string}|null)} [findIngredient] — optional
 *        resolver to attach a display name; falls back to the slug.
 */
export function donenessProteinsInRecipe(recipe, findIngredient) {
  const out = [];
  const seen = new Set();
  const ings = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  for (const ing of ings) {
    const id = ing?.ingredientId;
    if (!id || seen.has(id)) continue;
    if (!isDonenessProtein(id)) continue;
    seen.add(id);
    const resolved = typeof findIngredient === "function" ? findIngredient(id) : null;
    out.push({
      canonicalId: id,
      name: resolved?.name || ing?.item || id.replace(/_/g, " "),
    });
  }
  return out;
}

export const DONENESS_REVERSE_SEAR_THRESHOLD_IN = REVERSE_SEAR_THRESHOLD_IN;

// Cook-verb test for locating the step that sears/cooks the protein.
const COOK_VERB_RE = /\b(cook|sear|grill|fry|saut[eé]|roast|broil|pan[\s-]?fry|griddle|brown|char)\b/i;

/** Index of the step that cooks `canonicalId`, or -1. Mirrors the
 *  heuristic in meatDoneness.js: a cook-verb step that names the protein,
 *  else the longest-timer cook step. */
export function findDonenessCookStepIndex(recipe, canonicalId) {
  const steps = Array.isArray(recipe?.steps) ? recipe.steps : [];
  const bare = String(canonicalId || "").replace(/_/g, " ");
  const tokens = [bare, ...bare.split(/\s+/).filter((p) => p.length >= 3)];
  for (let i = 0; i < steps.length; i += 1) {
    const txt = `${steps[i]?.title || ""} ${steps[i]?.instruction || ""}`.toLowerCase();
    if (!COOK_VERB_RE.test(txt)) continue;
    if (tokens.some((t) => txt.includes(t))) return i;
  }
  // Fallback: longest-timer cook step.
  let bestIdx = -1;
  let bestTimer = -1;
  for (let i = 0; i < steps.length; i += 1) {
    const txt = `${steps[i]?.title || ""} ${steps[i]?.instruction || ""}`;
    if (!COOK_VERB_RE.test(txt)) continue;
    const sec = Math.max(
      Number(steps[i]?.timer) || 0,
      ...(Array.isArray(steps[i]?.timers) ? steps[i].timers.map((t) => Number(t?.seconds) || 0) : [0]),
    );
    if (sec > bestTimer) { bestTimer = sec; bestIdx = i; }
  }
  return bestIdx;
}

const TEMP_IN_TEXT_RE = /\b\d{2,3}\s*°?\s*f\b/i;

/**
 * Bake the cook's thickness + doneness answers into a recipe's cook
 * steps: inject the target internal-temp cue into the step's doneCue and
 * retune the sear timer to the thickness-scaled estimate. Pure +
 * immutable — returns a new recipe (or the same one when there's nothing
 * to apply). Safe to call every render.
 *
 * @param {object} recipe
 * @param {Object.<string,{thicknessIn:number,donenessId:string}>} answers
 *        keyed by canonicalId
 */
export function applyDonenessToRecipe(recipe, answers) {
  if (!recipe || !answers || typeof answers !== "object") return recipe;
  const keys = Object.keys(answers);
  if (keys.length === 0 || !Array.isArray(recipe.steps)) return recipe;

  let steps = null; // lazy clone — only allocate when we actually change a step
  for (const canonicalId of keys) {
    const ans = answers[canonicalId];
    if (!ans) continue;
    const g = donenessGuidance({
      canonicalId,
      thicknessIn: ans.thicknessIn,
      donenessId: ans.donenessId,
    });
    if (!g) continue;
    const idx = findDonenessCookStepIndex(recipe, canonicalId);
    if (idx < 0) continue;
    if (!steps) steps = [...recipe.steps];
    const step = { ...steps[idx] };

    // Temp cue → doneCue. Replace any pre-existing temp the recipe carried
    // (the cook's chosen doneness is now authoritative) but keep non-temp
    // doneCue prose.
    const priorCue = typeof step.doneCue === "string" ? step.doneCue : "";
    const cueWithoutTemp = priorCue.replace(TEMP_IN_TEXT_RE, "").replace(/\s{2,}/g, " ").trim();
    step.doneCue = cueWithoutTemp ? `${cueWithoutTemp} — ${g.tempCue}` : g.tempCue;

    // Timer → the thickness-scaled estimate. Two sear stages (one per
    // side) for searable cuts; a reverse-sear cut keeps a single longer
    // window and leans on the thermometer. Shellfish stay as a single
    // short timer.
    if (g.reverseSear) {
      step.timer = g.totalSec;
      step.timers = undefined;
      step.donenessNote = `That's a thick cut (${fmtIn(g.thicknessIn)}). Reverse-sear: oven low until ~10°F under, then sear hot to finish.`;
    } else if (g.family === "shellfish") {
      step.timer = g.perSideSec;
      step.timers = undefined;
    } else {
      step.timers = [
        { label: "Sear side 1", seconds: g.perSideSec },
        { label: "Sear side 2", seconds: g.perSideSec },
      ];
      step.timer = g.totalSec;
      // Thick patties can burn the crust before the center hits the safe
      // temp — tell the cook to cover / drop the heat rather than push the
      // sear longer.
      if (g.patty && g.thicknessIn >= 1.0) {
        step.donenessNote = `Thick patty (${fmtIn(g.thicknessIn)}) — after searing both sides, cover the pan or drop to medium so the center reaches ${g.doneness.finalF}°F before the crust burns.`;
      }
    }
    // Prose tracks the displayed amount (data-adaptability rule): rewrite
    // the step's instruction so a "3 minutes per side" no longer
    // contradicts the retuned timer + preview the cook just set.
    if (typeof step.instruction === "string") {
      step.instruction = rewriteSearProse(step.instruction, g);
    }

    step.donenessApplied = { canonicalId, ...g.doneness, thicknessIn: g.thicknessIn };
    steps[idx] = step;
  }
  return steps ? { ...recipe, steps } : recipe;
}

// Rewrite the time in a sear step's prose to match the thickness-scaled
// estimate. Anchored to the "per side" / "each side" phrasing so we never
// grab an unrelated number; falls back to a clause replace for total-time
// "sear for N minutes" phrasings. Leaves prose alone when neither matches
// — the doneCue + timer still carry the adjustment.
function rewriteSearProse(instruction, g) {
  const perSideMin = g.perSideSec / 60;
  const perSideWords = perSideMin >= 1
    ? `${perSideMin % 1 === 0 ? perSideMin : perSideMin.toFixed(1)} minute${perSideMin === 1 ? "" : "s"}`
    : `${g.perSideSec} seconds`;
  const totalMin = g.totalSec / 60;
  const totalWords = totalMin >= 1
    ? `${totalMin % 1 === 0 ? totalMin : totalMin.toFixed(1)} minute${totalMin === 1 ? "" : "s"}`
    : `${g.totalSec} seconds`;

  // "3 minutes per side", "3-4 min per side", "3 to 4 minutes a side".
  const perSideRe = /\b\d+(?:\.\d+)?\s*(?:[-–—]|to|or)?\s*\d*(?:\.\d+)?\s*(?:minutes?|mins?)\s+(?:per|a|each)\s+side/i;
  if (perSideRe.test(instruction)) {
    return instruction.replace(perSideRe, `${perSideWords} per side`);
  }
  // "sear for 8 minutes", "cook 8-10 minutes" (total) — only when a sear/
  // cook verb leads, so we don't rewrite a rest/marinade time.
  const totalRe = /\b(sear|cook|grill|fry|griddle|pan[\s-]?fry)(\s+(?:it|the\s+\w+|for))?\s+\d+(?:\.\d+)?\s*(?:[-–—]|to|or)?\s*\d*(?:\.\d+)?\s*(minutes?|mins?)/i;
  if (totalRe.test(instruction)) {
    return instruction.replace(totalRe, (m, verb) => `${verb} for ${totalWords}`);
  }
  return instruction;
}

function fmtIn(inches) {
  const n = Number(inches) || 0;
  return n % 1 === 0 ? `${n}"` : `${n.toFixed(2).replace(/0$/, "")}"`;
}

// Families whose dishes are meant to be eaten fresh and should never
// carry leftover/reheat instructions — a medium-rare steak or seared
// tuna doesn't get microwaved. Pork chops and chicken DO reheat fine, so
// they're deliberately excluded.
const SERVED_FRESH_FAMILIES = new Set(["red_meat", "fish", "tuna"]);

/** True when the recipe's protein is a served-fresh cut (steak / seafood). */
export function recipeServedFresh(recipe) {
  const ings = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  for (const ing of ings) {
    const fam = donenessFamilyFor(ing?.ingredientId);
    if (fam && SERVED_FRESH_FAMILIES.has(fam)) return true;
  }
  return false;
}

/**
 * Remove a bogus reheat/leftover block from a served-fresh recipe. This
 * cleans up rows saved while the chained-enrichment bug force-attached
 * reheat to every recipe. Conservative: only strips when the dish is
 * clearly served fresh AND isn't a batch component (`produces` ingredient
 * like stock/sauce). Returns the same object when nothing should change,
 * so callers can detect a no-op by identity.
 */
export function stripBogusReheat(recipe) {
  if (!recipe || !recipe.reheat) return recipe;
  if (recipe.produces?.kind === "ingredient") return recipe;
  if (!recipeServedFresh(recipe)) return recipe;
  const { reheat, ...rest } = recipe;
  return rest;
}
